import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * The workspace's lower-left corner, where the rail's persistent avatar
 * stands while the rail is collapsed. A page claims the corner while
 * something of its own stands there (the canvas's shed toolbar, its chat
 * launcher beside a full-width detail pane); the avatar stands down while
 * any claim holds. Claims are counted, so two claimants never clobber each
 * other's release.
 */
interface LowerLeftCornerValue {
  claimed: boolean;
  /** Takes a claim; the returned function releases it. */
  claim: () => () => void;
}

const LowerLeftCornerContext = createContext<LowerLeftCornerValue>({
  claimed: false,
  claim: () => () => {},
});

export function LowerLeftCornerProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setClaims((n) => n - 1);
    };
  }, []);
  const value = useMemo(
    () => ({ claimed: claims > 0, claim }),
    [claims, claim],
  );
  return (
    <LowerLeftCornerContext.Provider value={value}>
      {children}
    </LowerLeftCornerContext.Provider>
  );
}

export function useLowerLeftCornerClaimed(): boolean {
  return useContext(LowerLeftCornerContext).claimed;
}

/** Holds a claim on the lower-left corner for as long as `active` is true. */
export function useClaimLowerLeftCorner(active: boolean): void {
  const { claim } = useContext(LowerLeftCornerContext);
  useEffect(() => (active ? claim() : undefined), [active, claim]);
}
